import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { FlagIcon, PlayIcon } from "@phosphor-icons/react";
import type { SavedRun } from "../../../../../packages/contracts/src/workspace";
import type { MethodOverview } from "../../../../../packages/contracts/src/dashboard";
import { Button } from "../ui/button";
import { CancelRun } from "../../CancelRun";
import { runStatus } from "../../MethodNavigation";
import { useWorkspacePrefetch } from "../../workspace-queries";
import { RunBar, squareClass } from "./RunSquares";
import { RunPopover } from "./RunPopover";
import { ReportPopover } from "./ReportPopover";
import { dayLabel, duration, when } from "./format";
// The day header gives the day, so a row shows only its time.
const clock = (value: string) => new Date(value).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

/** What the Runs panel knows about a Method: its live version and the facts the run popover shows. */
export type RunMethod = { id: string; name: string; liveVersionId: string | null; liveNumber: number | null; overview: MethodOverview | null };
type Group = { key: string; runs: SavedRun[] };
type Line = { day: string } | { run: SavedRun } | { group: Group };

const GAP_MS = 5 * 60_000;
const stopped = (status: string) => status === "failed";
const device = (run: SavedRun) => run.summary?.content === "device";
const time = (value: string) => new Date(value).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

/** The text a person can find a run by: its label, Method, input values and result. */
export function runSearchText(run: SavedRun) {
  return [run.run_label, run.workflow_name, ...(run.summary?.inputs ?? []).map(([, value]) => value), run.summary?.result_preview].filter(Boolean).join(" ").toLowerCase();
}

/**
 * Day headers, then runs newest first. Three or more runs of one Method in a row, each less than 5 minutes from the
 * next, become one group. Queued runs are never grouped.
 */
export function runLines(runs: SavedRun[], now = new Date()): Line[] {
  const sorted = [...runs].sort((a, b) => b.created_at.localeCompare(a.created_at));
  const lines: Line[] = [];
  let day = "";
  for (let index = 0; index < sorted.length;) {
    const run = sorted[index]!;
    const label = dayLabel(run.created_at, now);
    if (label !== day) { lines.push({ day: label }); day = label; }
    let end = index + 1;
    while (!run.pending && end < sorted.length) {
      const next = sorted[end]!, previous = sorted[end - 1]!;
      if (next.pending || next.workflow_id !== run.workflow_id || dayLabel(next.created_at, now) !== label
        || Date.parse(previous.created_at) - Date.parse(next.created_at) >= GAP_MS) break;
      end++;
    }
    if (end - index >= 3) { lines.push({ group: { key: run.id, runs: sorted.slice(index, end) } }); index = end; }
    else { lines.push({ run }); index++; }
  }
  return lines;
}

export function RunsTable({ runs, methods, busy, children }: { runs: SavedRun[]; methods: Map<string, RunMethod>; busy: boolean; children?: ReactNode }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string) => setOpen(current => { const next = new Set(current); if (!next.delete(key)) next.add(key); return next; });
  return <table className="runs-panel">
    <thead><tr className="r-row r-head"><th scope="col">Run</th><th scope="col">Inputs</th><th scope="col">Result</th><th scope="col">Time</th><th scope="col">Started</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
    {children ?? <tbody aria-busy={busy || undefined}>{runLines(runs).map(line => "day" in line
      ? <tr key={`day-${line.day}`} className="r-day"><th colSpan={6} scope="colgroup">{line.day}</th></tr>
      : "run" in line ? <RunRow key={line.run.id} run={line.run} method={methods.get(line.run.workflow_id)} />
      : <GroupRows key={line.group.key} group={line.group} method={methods.get(line.group.runs[0]!.workflow_id)} open={open.has(line.group.key)} toggle={() => toggle(line.group.key)} />)}
    </tbody>}
  </table>;
}

function RunRow({ run, method, child = false }: { run: SavedRun; method?: RunMethod; child?: boolean }) {
  const prefetch = useWorkspacePrefetch();
  const summary = run.summary ?? null;
  const older = method?.liveVersionId && run.workflow_version_id !== method.liveVersionId
    ? `Older version ${run.version_number}${method.liveNumber && method.liveNumber > run.version_number ? ` · live is ${method.liveNumber}` : ""}` : null;
  const sub = [run.run_label ? run.workflow_name : null, older].filter(Boolean).join(" · ");
  const title = run.run_label || run.workflow_name;
  const visit = () => prefetch.run(run.id, run.workflow_id);
  return <tr className={`r-row${child ? " child" : ""}`}>
    <td className="r-t">
      {run.pending ? <strong title="A worker has not picked up this run yet.">{title}</strong>
        : <Link className="r-link" to="/runs/$runId" params={{ runId: run.id }} onMouseEnter={visit} onFocus={visit} title={title}><strong>{title}</strong></Link>}
      {(sub || (run.started_by && run.started_by !== "You")) && <small>{sub}{run.started_by && run.started_by !== "You" && <span className="r-tag">{run.started_by}</span>}</small>}
    </td>
    <td><RunInputs run={run} /></td>
    <td><RunResult run={run} /></td>
    <td className="r-num">{summary?.duration_ms != null ? duration(summary.duration_ms) : "—"}</td>
    <td className="r-num"><time dateTime={run.created_at} title={when(run.created_at)}>{clock(run.created_at)}</time></td>
    <td className="r-act">{!run.pending && <RunActions run={run} method={method} />}</td>
  </tr>;
}

function RunActions({ run, method }: { run: SavedRun; method?: RunMethod }) {
  if (run.production && ["queued", "running", "waiting"].includes(run.status)) return <CancelRun run={run} />;
  const summary = run.summary ?? null, methodName = method?.name ?? run.workflow_name;
  const keys = summary?.inputs.map(([key]) => key) ?? [];
  return <>
    <ReportPopover target={{ methodId: run.workflow_id, methodName, device: device(run), runId: run.id, runCreatedAt: run.created_at }}
      trigger={<Button variant="ghost" size="icon" aria-label="Something is wrong with this result" title="Something is wrong with this result"><FlagIcon size={15} /></Button>} />
    <RunPopover target={{ methodId: run.workflow_id, methodName, versionId: method?.liveVersionId ?? run.workflow_version_id, facts: method?.overview?.facts ?? null, typicalMs: method?.overview?.typical_ms ?? null }}
      again={{ runId: run.id, createdAt: run.created_at, keys, more: Math.max(0, (summary?.inputs_count ?? 0) - keys.length), device: device(run), cloud: method?.overview?.placement === "cloud" }}
      trigger={<Button variant="ghost" size="icon" aria-label="Run again" title="Run again"><PlayIcon size={15} /></Button>} />
  </>;
}

function RunInputs({ run }: { run: SavedRun }) {
  const summary = run.summary;
  if (run.pending || !summary) return null;
  if (summary.content === "device") return <div className="r-in"><span className="none">Kept on your computer</span></div>;
  if (!summary.inputs_count) return <div className="r-in"><span className="none">No inputs recorded</span></div>;
  return <InputLines inputs={summary.inputs} count={summary.inputs_count} />;
}
function InputLines({ inputs, count }: { inputs: [string, string][]; count: number }) {
  const shown = inputs.slice(0, 2), more = count - shown.length;
  return <div className="r-in">{shown.map(([key, value], index) => <div key={key}>
    <span className="k">{key}</span><span className="v" title={value}>{value}</span>
    {index === shown.length - 1 && more > 0 && <span className="more">+{more} more</span>}
  </div>)}</div>;
}

function RunResult({ run }: { run: SavedRun }) {
  const summary = run.summary ?? null;
  const status = run.pending ? "queued" : run.status;
  const square = <i className={`run-square ${squareClass(status)}`} title={runStatus(status).label} />;
  if (run.pending) return <div className="r-out">{square}Queued</div>;
  if (status === "running") return <div className="r-out">{square}Running{summary?.current_step ? ` · ${summary.current_step}` : ""}</div>;
  if (stopped(status)) {
    const where = summary?.failure?.step_name ?? summary?.stopped_step?.name;
    const text = `${where ? `Failed at ${where}` : runStatus(status).label}${summary?.failure ? `: ${summary.failure.headline}` : ""}`;
    return <div className="r-out bad" title={text}>{square}{text}</div>;
  }
  if (status !== "succeeded") return <div className="r-out">{square}{runStatus(status).label}</div>;
  const text = summary?.result_preview ?? (summary?.content === "device" ? "Kept on your computer" : null);
  return <div className="r-out" title={text ?? undefined}>{square}{summary?.content === "device" && !summary.result_preview ? <span className="faint">{text}</span> : text}</div>;
}

function GroupRows({ group, method, open, toggle }: { group: Group; method?: RunMethod; open: boolean; toggle: () => void }) {
  const runs = group.runs, newest = runs[0]!, oldest = runs.at(-1)!;
  const name = method?.name ?? newest.workflow_name;
  const versions = [...new Set(runs.map(run => run.version_number))].sort((a, b) => a - b);
  const edits = versions.length - 1;
  const failed = runs.filter(run => stopped(run.status));
  const steps = failed.flatMap(run => run.summary?.stopped_step ? [run.summary.stopped_step.name] : []);
  const step = steps.sort((a, b) => steps.filter(s => s === b).length - steps.filter(s => s === a).length)[0];
  const durations = runs.flatMap(run => run.summary?.duration_ms != null ? [run.summary.duration_ms] : []).sort((a, b) => a - b);
  const median = durations.length ? durations[Math.floor(durations.length / 2)]! : null;
  return <>
    <tr className="r-row burst" onClick={toggle}>
      <td className="r-t">
        <button type="button" className="r-toggle" aria-expanded={open} onClick={event => { event.stopPropagation(); toggle(); }}>
          <strong><span className="caret" aria-hidden="true">{open ? "▾" : "▸"}</span>{name} · {runs.length} runs</strong>
        </button>
        <small>{time(oldest.created_at)} – {time(newest.created_at)} · {edits
          ? <>edited {edits === 1 ? "once" : edits === 2 ? "twice" : `${edits} times`} <span className="vp">{versions.map((version, index) => <span key={version}>
            {index > 0 && "→"}<em className={runs.some(run => run.version_number === version && stopped(run.status)) ? "f" : undefined}>v{version}</em></span>)}</span></>
          : `version ${versions[0]}`}</small>
      </td>
      <td><GroupInputs runs={runs} /></td>
      <td><div className="r-out"><RunBar statuses={[...runs].reverse().map(run => run.status)} />{failed.length > 0 && <span className="bad">{failed.length} failed{step ? ` at ${step}` : ""}</span>}</div></td>
      <td className="r-num">{median !== null ? duration(median) : "—"}</td>
      <td className="r-num"><time dateTime={newest.created_at} title={when(newest.created_at)}>{clock(newest.created_at)}</time></td>
      <td />
    </tr>
    {open && runs.map(run => <RunRow key={run.id} run={run} method={method} child />)}
  </>;
}

function GroupInputs({ runs }: { runs: SavedRun[] }) {
  if (runs.every(device)) return <div className="r-in"><span className="none">Kept on your computer</span></div>;
  const withInputs = runs.find(run => run.summary?.inputs.length);
  if (!withInputs) return <div className="r-in"><span className="none">No inputs recorded</span></div>;
  const [name] = withInputs.summary!.inputs[0]!;
  const values = new Set(runs.map(run => run.summary?.inputs.find(([key]) => key === name)?.[1] ?? ""));
  if (values.size > 1) return <div className="r-in"><span className="none">{values.size} different values of <span className="k">{name}</span></span></div>;
  return <InputLines inputs={withInputs.summary!.inputs} count={withInputs.summary!.inputs_count} />;
}
